import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isIntercomAuthPending, setIntercomLauncherHidden } from '@/services/intercom';

declare global {
  interface Window {
    // @ts-ignore
    Intercom?: any;
    // @ts-ignore
    intercomSettings?: any;
  }
}

/**
 * El 'shutdown' solo no alcanza para esconder el botón: cualquier 'update' posterior re-inicializa el
 * messenger y lo devuelve. El widget además baja DIFERIDO (ver index.html),
 * así que puede bootear después del shutdown.
 * hide_default_launcher viaja en intercomSettings y en el update, así que aguanta las dos vías.
 */
export const useIntercom = (shouldDisplay: boolean = true) => {
  useEffect(() => {
    // Check if we're in a browser environment and DOM is ready
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    setIntercomLauncherHidden(!shouldDisplay);

    // Wait for DOM to be ready
    const initializeIntercom = () => {
      // Check if Intercom is available and properly configured
      if (typeof window.Intercom !== 'undefined') {
        if (!shouldDisplay) {
          // Shutdown Intercom
          try {
            window.Intercom('shutdown');
          } catch {
            // Silently handle Intercom shutdown errors
          }

          // Remove the Intercom frame
          const frame = document.querySelector('#intercom-frame');
          if (frame) {
            frame.remove();
          }

          // Remove the Intercom container
          const container = document.querySelector('#intercom-container');
          if (container) {
            container.remove();
          }
        } else if (!isIntercomAuthPending()) {
          // Restart Intercom if it was previously shutdown
          // Only boot if intercomSettings is available and not null
          if (window.intercomSettings && typeof window.intercomSettings === 'object') {
            try {
              window.Intercom('boot', window.intercomSettings);
            } catch {
              // Silently handle Intercom boot errors
            }
          }
        }
      }
    };

    // If DOM is already ready, initialize immediately
    if (document.readyState === 'complete') {
      initializeIntercom();
    } else {
      // Otherwise wait for load event
      window.addEventListener('load', initializeIntercom);
    }

    return () => {
      window.removeEventListener('load', initializeIntercom);

      // Cleanup when component unmounts
      if (!shouldDisplay) {
        setIntercomLauncherHidden(false);

        if (typeof window.Intercom !== 'undefined') {
          try {
            window.Intercom('shutdown');
          } catch {
            // Silently handle Intercom shutdown errors
          }
        }
      }
    };
  }, [shouldDisplay]);
};

/**
 * Hook to auto-open Intercom chat via URL query parameters.
 *
 * Supported query params:
 * - `ic_message`: Opens Intercom with a pre-filled message
 * - `ic_open`: Opens Intercom messenger (set to "true")
 *
 * Example URLs:
 * - https://app.agora.com/?ic_message=Hola%2C%20necesito%20ayuda
 * - https://app.agora.com/?ic_open=true
 */
export const useIntercomAutoOpen = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.Intercom === 'undefined') {
      return;
    }

    const message = searchParams.get('ic_message');
    const shouldOpen = searchParams.get('ic_open');

    if (!message && !shouldOpen) {
      return;
    }

    // Wait for Intercom to be fully loaded
    const openIntercom = () => {
      try {
        if (message) {
          window.Intercom('showNewMessage', decodeURIComponent(message));
        } else if (shouldOpen === 'true') {
          window.Intercom('show');
        }

        // Clean up URL params after opening
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('ic_message');
        newParams.delete('ic_open');
        setSearchParams(newParams, { replace: true });
      } catch {
        // Silently handle errors
      }
    };

    // Small delay to ensure Intercom widget is ready
    const timeoutId = setTimeout(openIntercom, 1500);

    return () => clearTimeout(timeoutId);
  }, [searchParams, setSearchParams]);
};
